import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

import { ProductCategoriesBiList_productCategories_edges_node } from "../../types/ProductCategoriesBiList";
const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "10em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "5em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "20em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },
  { name: "ProductCategoriesBiList" }
);

export interface ProductCategoriesBiListProps extends ListProps {
  categories: ProductCategoriesBiList_productCategories_edges_node[];
}

export const ProductCategoriesBiList: React.FC<ProductCategoriesBiListProps> = ({
  categories,
  onNextPage,
  onPreviousPage,
  onRowClick,
  onUpdateListSettings,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 3;
  const classes = useStyles({});

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Category Code" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Category Name" />
            </TableCellHeader>
            <TableCellHeader className={classes.colNarrow}>
              <FormattedMessage defaultMessage="Level" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {categories &&
            renderCollection(
              categories,
              category => (
                <TableRow
                  className={!!category ? classes.tableRow : undefined}
                  hover={!!category}
                  key={category ? category.id : "skeleton"}
                  onClick={
                    category
                      ? onRowClick(btoa(`Category:${atob(category.id)}`))
                      : undefined
                  }
                >
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => category.category_code,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => category.category_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(() => category.level, <Skeleton />)}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No categories found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default ProductCategoriesBiList;
